import * as React from 'react'
import Icon from '../../../../common/src/components/Icon'
import { Link } from '../../../../common/src/components/Link'
import { Spacer } from '../../../../common/src/components/core'
import { TestIds } from '../../../../common/src/data/testids'
import { fire, KeyAction } from '../../../../common/src/keyboard'
import memoize from '../../../../common/src/memoize'
import { FetchThreadQuery } from '../../../../common/src/types.gen'
import { getTaskPath } from '../route'
import LazyComponent from './LazyComponent'
import { TaskIcon } from './task'

interface LinkifiedTextProps {
  text: string
  messageLinks?: FetchThreadQuery['thread']['messagesWithLinks']['messageLinks']
}

/**
 * A wrapper component for `LinkifiedText` to minimize linkification because
 * it is an expensive operation.
 */
export class LazyLinkifiedText extends React.Component<LinkifiedTextProps> {
  /**
   * Didn't use `React.PureComponent` because it only does shallow comparison on props so
   * this component will re-renders even when `LazyLinkifiedTextProps#links` are not changing.
   * Use `React.Component` and explicitly tell it when to re-render because linkification
   * is expensive and we want to minimize the call to `LazyLinkifiedText#renderLinkifiedText`.
   */
  shouldComponentUpdate(nextProps: LinkifiedTextProps) {
    const { text, messageLinks } = this.props
    const nextText = nextProps.text
    const nextMessageLinks = nextProps.messageLinks
    // check length is enough because links rarely change
    return text !== nextText || messageLinks?.length !== nextMessageLinks?.length
  }

  render() {
    const { text } = this.props
    return <LazyComponent componentGenerator={this.renderLinkifiedText} placeholder={<>{text}</>} stayVisible />
  }

  private renderLinkifiedText = () => <LinkifiedText {...this.props} />
}

/**
 * Linkify url, task display name and address given a message, so users can click them and jump to task pages.
 * This allows faster operator workflow.
 */
export default function LinkifiedText(props: LinkifiedTextProps) {
  const { text, messageLinks } = props

  const httpre = /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_+.~#?&//=,!]*)/g
  let match = httpre.exec(text)

  if (match == null && messageLinks == null) {
    return <>{text}</>
  }

  let sortedMessageLinks = null
  let re = null
  if (messageLinks != null) {
    // prefers longer text match
    sortedMessageLinks = messageLinks.slice().sort((a, b) => b.phrase.length - a.phrase.length)

    // smaller index has higher precedence during linkification
    const phrases = sortedMessageLinks.map(messageLink => escapeRegExp(messageLink.phrase))

    // `\b` to match word boundaries and `g` to match all.
    // `(${phrases.join('|')})` to match any phrase in phrases
    // e.g. (phrase1|phrase2|phrase3)
    re = new RegExp(`\\b(${phrases.join('|')})\\b`, 'g')
  }

  const elems = []
  let lastIndex = 0
  while (match != null) {
    const substr = text.substring(lastIndex, match.index)
    lastIndex = httpre.lastIndex
    elems.push(linkifyText(sortedMessageLinks, re, substr))

    // Linkify Url
    const linkUrl = match[0]
    const linkText = shortenUrl(linkUrl)
    elems.push(
      <Link key={elems.length} href={linkUrl} noTab opensNewTab={true}>
        {linkText}
      </Link>
    )
    match = httpre.exec(text)
  }
  elems.push(linkifyText(sortedMessageLinks, re, text.substring(lastIndex)))

  return (
    <>
      {elems.map((el, i) => (
        <span key={i}>{el}</span>
      ))}
    </>
  )
}

function linkifyText(
  sortedMessageLinks: FetchThreadQuery['thread']['messagesWithLinks']['messageLinks'] | null,
  re: RegExp | null,
  text: string
) {
  if (sortedMessageLinks == null || re == null) {
    return <>{text}</>
  }

  // split text by "words to replace"
  const splittedText = text.split(re)

  // linkification
  const messageLinkMap: { [key: string]: FetchThreadQuery['thread']['messagesWithLinks']['messageLinks'][0] } = {}
  sortedMessageLinks.forEach(l => (messageLinkMap[l.phrase] = l))

  const parts = splittedText
    .filter(phrase => phrase != null)
    .map((phrase, i) =>
      messageLinkMap.hasOwnProperty(phrase) ? (
        <MessageLink key={i} messageLink={messageLinkMap[phrase]} />
      ) : (
        <span key={i}>{phrase}</span>
      )
    )
  return <>{parts}</>
}

function shortenUrl(url: string) {
  return url.length < 30 ? url : url.substring(0, 27) + '…'
}

interface MessageLinkProps {
  messageLink: FetchThreadQuery['thread']['messagesWithLinks']['messageLinks'][0]
}

class MessageLink extends React.PureComponent<MessageLinkProps> {
  private handlers: { [key: string]: () => void } = {}

  render() {
    const { messageLink } = this.props
    const { phrase } = messageLink

    // eslint-disable-next-line no-underscore-dangle
    if (messageLink.link.__typename === 'FloorPlanLink') {
      const leasingTask = messageLink.link.leasingTask
      return (
        <Link
          onClick={memoize(this.handlers, messageLink.link.leasingTask.id, () => () => () => {
            console.log('Clicked Floorplan Link in Message')
            fire(KeyAction.togglePricing, {
              showPricingTaskId: leasingTask.id,
              showPricingTitle: leasingTask.displayName,
            })
          })}
          data-testid={TestIds.floorPlanLink}
        >
          <Icon name="floorplan" />
          <Spacer width={4} />
          {phrase}
        </Link>
      )
      // eslint-disable-next-line no-underscore-dangle
    } else if (messageLink.link.__typename === 'LeasingTaskLink') {
      return (
        <Link
          href={getTaskPath(messageLink.link.leasingTask.id)}
          opensNewTab={true}
          onClick={memoize(this.handlers, messageLink.link.leasingTask.id, () => () => {
            console.log('Clicked Task Link in Message')
          })}
          data-testid={TestIds.leasingTaskLink}
        >
          <TaskIcon task={messageLink.link.leasingTask} />
          <Spacer width={4} />
          {phrase}
        </Link>
      )
    }

    console.error('Unsupported link', messageLink)
    return <>{phrase}</>
  }
}

function escapeRegExp(str: string) {
  // $& means the whole matched string
  return str.replace(/[.*+\-?^${}()|[\]\\]/g, '\\$&')
}
